import * as PIXI from "pixi.js";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { useIsMobile } from "../../../shared/hooks/useIsMobile";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { CharacterAnim } from "../PioneerGame/index.enums";

interface UseCameraParams {
  currentAnim?: string | null;
  cameraRef: { current: PIXI.Container | null };
  canvasWidth: number;
  gameState?: GameState;
}

interface CameraPosition {
  zoom: number;
  x: number;
  y: number;
}

type ProfileKey = "mobile" | "desktop" | "desktop2k";

interface AnimationConfig {
  zoomFactor: number;
  offsetY: number;
  duration: number;
  delay?: number;
}

interface CameraProfile {
  base: { zoom: number; xPercent: number; y: number };
  animations: Record<"climb" | "fall" | "idle", AnimationConfig>;
}

const CAMERA_PROFILES: Record<ProfileKey, CameraProfile> = {
  mobile: {
    base: { zoom: 0.85, xPercent: 0.4, y: 380 },
    animations: {
      idle: { zoomFactor: 1, offsetY: 0, duration: 600 },
      climb: { zoomFactor: 1.05, offsetY: 10, duration: 1000 },
      fall: { zoomFactor: 1.05, offsetY: -150, duration: 1000, delay: 600 },
    },
  },
  desktop: {
    base: { zoom: 1.25, xPercent: 0.4, y: 600 },
    animations: {
      idle: { zoomFactor: 1, offsetY: 0, duration: 600 },
      climb: { zoomFactor: 1, offsetY: -20, duration: 1000 },
      fall: { zoomFactor: 1, offsetY: -200, duration: 1000, delay: 600 },
    },
  },
  desktop2k: {
    base: { zoom: 1.8, xPercent: 0.4, y: 900 },
    animations: {
      idle: { zoomFactor: 1, offsetY: 0, duration: 600 },
      climb: { zoomFactor: 1, offsetY: -20, duration: 1000 },
      fall: { zoomFactor: 1, offsetY: -200, duration: 1000, delay: 600 },
    },
  },
};

const ANIM_MAP: Record<string, "climb" | "fall" | "idle"> = {
  [CharacterAnim.CLIMB]: "climb",
  [CharacterAnim.FALL]: "fall",
  [CharacterAnim.IDLE]: "idle",
};

const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export default function useCamera({
  currentAnim,
  cameraRef,
  canvasWidth,
  gameState,
}: UseCameraParams): { current: CameraPosition } {
  const isMobile = useIsMobile();
  const isLarge = useIsMobile({ breakpoint: 1920, compare: "gte" });
  const profileKey: ProfileKey = isMobile
    ? "mobile"
    : isLarge
      ? "desktop2k"
      : "desktop";
  const profile = CAMERA_PROFILES[profileKey];

  const getBasePosition = useCallback((): CameraPosition => {
    return {
      zoom: profile.base.zoom,
      x: canvasWidth * profile.base.xPercent,
      y: profile.base.y,
    };
  }, [profile.base.zoom, profile.base.xPercent, profile.base.y, canvasWidth]);

  const [current, setCurrent] = useState(getBasePosition);

  const currentRef = useRef(current);
  const animationIdRef = useRef<number | null>(null);
  const timeoutIdRef = useRef<number | null>(null);
  const lastAnimRef = useRef<string | null>(null);
  const prevRef = useRef({ gameState, profileKey, canvasWidth });

  const cancelAll = useCallback(() => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const apply = useCallback(
    (pos: CameraPosition) => {
      cameraRef.current?.scale.set(pos.zoom);
      cameraRef.current?.position.set(pos.x, pos.y);
      currentRef.current = pos;
      setCurrent(pos);
    },
    [cameraRef]
  );

  const animateTo = useCallback(
    (target: CameraPosition, duration: number) => {
      cancelAll();
      const start = { ...currentRef.current };
      const startTime = performance.now();

      const step = (now: number) => {
        const t = easeOutQuart(Math.min((now - startTime) / duration, 1));
        apply({
          zoom: lerp(start.zoom, target.zoom, t),
          x: lerp(start.x, target.x, t),
          y: lerp(start.y, target.y, t),
        });

        if (t < 1) {
          animationIdRef.current = requestAnimationFrame(step);
        } else {
          animationIdRef.current = null;
        }
      };

      animationIdRef.current = requestAnimationFrame(step);
    },
    [cancelAll, apply]
  );

  const getTargetPosition = useCallback(
    (config: AnimationConfig): CameraPosition => {
      const base = getBasePosition();
      return {
        zoom: base.zoom * config.zoomFactor,
        x: base.x,
        y: base.y + config.offsetY,
      };
    },
    [getBasePosition]
  );

  const reset = useCallback(() => {
    cancelAll();
    apply(getBasePosition());
    lastAnimRef.current = null;
  }, [cancelAll, apply, getBasePosition]);

  useEffect(() => {
    const prev = prevRef.current;
    const isCountdownStart =
      gameState === GameStateEnum.COUNTDOWN &&
      prev.gameState !== GameStateEnum.COUNTDOWN;
    const isProfileOrSizeChange =
      prev.profileKey !== profileKey || prev.canvasWidth !== canvasWidth;

    prevRef.current = { gameState, profileKey, canvasWidth };

    if (isCountdownStart || isProfileOrSizeChange) {
      reset();
    }
  }, [gameState, profileKey, canvasWidth, reset]);

  useEffect(() => {
    const anim = currentAnim ?? null;
    if (lastAnimRef.current === anim || gameState === GameStateEnum.COUNTDOWN)
      return;
    lastAnimRef.current = anim;

    const animKey = anim ? ANIM_MAP[anim] : null;
    if (!animKey) return;

    const config = profile.animations[animKey];
    const target = getTargetPosition(config);

    if (config.delay) {
      timeoutIdRef.current = window.setTimeout(() => {
        animateTo(target, config.duration);
        timeoutIdRef.current = null;
      }, config.delay);
    } else {
      animateTo(target, config.duration);
    }

    return cancelAll;
  }, [
    currentAnim,
    gameState,
    profile.animations,
    getTargetPosition,
    animateTo,
    cancelAll,
  ]);

  return { current };
}
